<template>
  <label
    :class="{ active: isDragOver, disabled: disabled }"
    class="e-file-load-area"
    @dragover.prevent
    @dragenter.stop="onDragEnter"
    @dragleave.stop="onDragLeave"
    @drop.stop.prevent="onDrop"
  >

    <div class="e-file-load-area__counter" @click.stop>
      <slot name="counter" />
    </div>

    <div v-if="isError || errorMessage" class="e-file-load-area__error" data-form-error>
      <e-icon name="alert" :size="14" class="text-red mr-2 mt-1" />
      <div v-if="!errorMessage">
        <b class="text-red paragraph-xs mt-1">Файл не был загружен </b>
        <p class="paragraph-xs text-secondary">Файл, который вы предложили, не соответсвует требованиям.</p>
      </div>
      <div v-else class="text-red paragraph-xs mt-1">
        {{ errorMessage }}
      </div>
    </div>

    <input
      v-if="!disabled"
      ref="inputRef"
      :accept="accept"
      :multiple="multiple"
      type="file"
      @change="onInput"
      @click="clearInput"
    />

    <div class="e-file-load-area__content">
      <div class="e-file-load-area__warning">
        <slot name="warning" />
      </div>

      <v-img :src="disabled ? FileDisabled : File" width="80px" />
      
      <div class="paragraph-m_h mt-6 px-4 text-center" :class="{ 'text-disabled': disabled }">
        <slot name="text">
          <b>
            <span class="e-file-load-area__btn" :class="{ disabled: disabled }">
              Выберите
              <template v-if="multiple">файлы</template>
              <template v-else>файл</template>
            </span>
            <span :class="{ 'text-disabled': disabled }"> или перетащите сюда</span>
          </b>
        </slot>
      </div>

      <div v-if="maxSize" class="text-secondary mt-2 paragraph-s">
        <slot name="subtitle">
          {{ subtitle || `Максимальный размер ${formatBytes(maxSize)}` }}
        </slot>
      </div>
    </div>
  </label>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import File from '@/assets/img/common/file.svg';
import FileDisabled from '@/assets/img/common/file-disabled.svg';
import { EIcon } from '@/core/components/ui';
import { checkFileMimeType, formatBytes, getBiteFromMb } from '@/core/utils/file';

const props = withDefaults(
  defineProps<{
    accept?: string;
    validation?: (file: File) => boolean;
    multiple?: boolean;
    icon?: string;
    maxSize?: number; // bytes
    errorMessage?: string;
    subtitle?: string;
    disabled?: boolean;
  }>(),
  {
    maxSize: 1024 * 1024 * 5, // 5mb
  },
);

defineSlots<{
  text?: () => void;
  subtitle?: () => void;
  counter?: () => void;
  warning?: () => void;
}>();

const emit = defineEmits<{
  (event: 'upload', value: File[]): void;
}>();

const isDragOver = ref(false);

const onDragEnter = () => {
  if (props.disabled) return;
  isDragOver.value = true;
};
const onDragLeave = () => {
  isDragOver.value = false;
};

const isError = ref(false);
const inputRef = ref<HTMLInputElement>();

const processFiles = (files: File[]) => {
  if (!props.multiple) {
    const isValidFile = files.find((file) => {
      if (props.maxSize && file.size > getBiteFromMb(props.maxSize)) {
        return false;
      }

      if (props.accept && !checkFileMimeType(file, props.accept)) {
        return false;
      }

      return !props.validation || props.validation(file);
    });

    if (!isValidFile) {
      isError.value = true;
      return;
    }
  }

  emit('upload', files);
};

const onInput = () => {
  if (inputRef.value && inputRef.value.files) {
    processFiles(Array.from(inputRef.value.files));
  }
};

const clearInput = () => {
  const input = inputRef.value;
  if (input) {
    input.value = '';
  }
};

const onDrop = (event: DragEvent) => {
  event.preventDefault();
  
  if (props.disabled) return;

  onDragLeave();

  isError.value = false;

  if (!event.dataTransfer) {
    return;
  }

  const items = Array.from(event.dataTransfer.files);

  processFiles(Array.from(items));
};
</script>

<style scoped lang="scss">
.e-file-load-area {
  position: relative;

  display: flex;
  flex-direction: column;

  padding: 40px 16px;

  border-radius: $border-radius-l;
  @include dashed-border($border-color);

  transition: background-color $transition-duration;

  input {
    height: 0;
  }

  &__counter {
    position: absolute;
    top: 24px;
    right: 24px;
  }

  &__warning {
    margin-bottom: 16px;
  }

  &__error {
    display: flex;
    align-self: flex-start;

    margin-top: -24px;
    margin-bottom: 16px;
    margin-left: 16px;
  }

  &__btn {
    margin-top: 16px;
    margin-bottom: 28px;

    color: $btn-link-text-color;

    transition: color $transition-duration;

    &.disabled {
      color: $btn-primary-disabled-text-color;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    margin: auto;
  }

  @include hover {
    cursor: pointer;

    .e-file-load-area__btn:not(.disabled) {
      color: $btn-primary-pressed-color;
    }
  }

  & > * {
    pointer-events: none;
  }

  &__counter {
    pointer-events: auto;
  }

  &.active {
    background-color: $primary-light-color;
  }

  &.disabled {
    cursor: not-allowed;

    @include hover {
      cursor: not-allowed;
    }
  }

  input {
    opacity: 0;
  }
}

.text-disabled {
  color: $btn-primary-disabled-text-color !important;
}
</style>
